import { useEffect } from 'react';

import { Box, useMediaQuery, useTheme } from '@mui/material';

import { App, Maybe, NavigationLink, Page } from '@/types/generated';
import { useAppContext, useFacilityContext } from '@/context';

import HeaderDesktop from './HeaderDesktop';
import HeaderMobile from './HeaderMobile';
import { getHeaderWrapperStyles } from './Header.styles';

import type { IBanner } from '../Banner/Banner';
import type { IGoogleMap } from '../GoogleMap/GoogleMap';
export interface IHeader {
	/**
	 * Application data, containg all the information about the application
	 *
	 * @type {App}
	 * @memberof IHeader
	 */
	appData: App;
	/**
	 * Determines if the location cards will be used in the header or not
	 *
	 * @type {boolean}
	 * @memberof IHeader
	 */
	useHeaderLocationCards: boolean | null | undefined;
	/**
	 * Whether to show (and fetch) the office details in the header or not.
	 *
	 * @type {boolean}
	 * @memberof IHeader
	 */
	withOfficeDetails?: boolean;
	/**
	 * Whether to show the ODP links block in the header or not.
	 *
	 * @type {boolean}
	 * @memberof IHeader
	 */
	inlineNavigation?: boolean;
	topLinks?: boolean;
	logoPosition?: 'left' | 'center';
	navigationBar?: boolean;
	isFullScreenMenu?: boolean;
	withMenuCTA?: boolean;
	logo?: {
		dark: string;
		light: string;
	};
	hidePrimaryActionButton?: boolean;
	hideLocation?: boolean;
	showHeaderNavMenu?: boolean;
	logoHref?: string;
	locationNavigationLinks?: Array<NavigationLink | Page> | undefined;
	phoneNumber?: Maybe<NavigationLink>;
	showLanguageToggle?: boolean;
	/**
	 * Fixed Header
	 *
	 * when it is true, header won't disappear when scrolling down
	 * NOTE:
	 * Sticky header and Fixed header in most cases are the same.
	 * but sticky header is the one that disappears when scrolling down
	 * and reappears when scrolling up in our case today
	 *
	 * Default is false
	 *
	 * @type {boolean}
	 * @memberof IHeader
	 */
	withFixedHeader?: boolean;
	/**
	 * Hide hamburger menu button on mobile.
	 *
	 * @type {boolean}
	 * @memberof IHeader
	 */
	hideHamburgerMenuToggleButton?: boolean;
	/**
	 * GoogleMap data for Header
	 *
	 * @type {string}
	 * @memberof IHeader
	 */
	googleMapsForLocationCards?: IGoogleMap;
	/**
	 * Top Alert Banner for Header
	 *
	 * @type {ContentTypeRichText[]}
	 * @memberof IHeader
	 */
	topAlertBanner?: IBanner;
	/**
	 * Bottom Alert Banner for Header
	 *
	 * @type {ContentTypeRichText[]}
	 * @memberof IHeader
	 */
	bottomAlertBanner?: IBanner;
	/**
	 * Disable Top Alert Banner for Header
	 *
	 * @type {boolean}
	 * @memberof IHeader
	 */
	disableHeaderTopAlertBanner?: boolean | null;
	/**
	 * Disable Bottom Alert Banner for Header
	 *
	 * @type {boolean}
	 * @memberof IHeader
	 */
	disableHeaderBottomAlertBanner?: boolean | null;
}

export default function Header({
	appData,
	withOfficeDetails,
	inlineNavigation,
	topLinks,
	logoPosition,
	navigationBar,
	isFullScreenMenu,
	withMenuCTA,
	logo,
	hidePrimaryActionButton = false,
	hideLocation = false,
	showHeaderNavMenu = false,
	logoHref = '/',
	locationNavigationLinks,
	phoneNumber,
	withFixedHeader = false,
	showLanguageToggle,
	useHeaderLocationCards = false,
	hideHamburgerMenuToggleButton = false,
	googleMapsForLocationCards,
	topAlertBanner,
	bottomAlertBanner,
	disableHeaderTopAlertBanner = false,
	disableHeaderBottomAlertBanner = false,
}: Readonly<IHeader>) {
	const variant = appData?.header?.variant as Maybe<string>;
	const theme = useTheme();
	const isLargeDevice = useMediaQuery(theme.breakpoints.up('md'));
	const logoUrl = appData?.header?.darkHeader ? logo?.light : logo?.dark;
	const { officeInfo } = useFacilityContext();
	const { config } = useAppContext();
	const showMobileHeaderLocationBar = config?.featureFlags?.showMobileHeaderLocationBar;
	const showHeaderWithLocationNav = config.featureFlags.showHeaderWithLocationNav;

	const topBannerAlertMessages = isLargeDevice
		? topAlertBanner?.alertMessagesCollection?.items
		: topAlertBanner?.mobileAlertMessagesCollection?.items;
	const bottomBannerAlertMessages = isLargeDevice
		? bottomAlertBanner?.alertMessagesCollection?.items
		: bottomAlertBanner?.mobileAlertMessagesCollection?.items;
	const showTopAlertBannerMessages = !disableHeaderTopAlertBanner && (topBannerAlertMessages ?? []).length > 0;
	const showBottomAlertBannerMessages =
		!disableHeaderBottomAlertBanner && (bottomBannerAlertMessages ?? []).length > 0;

	useEffect(() => {
		if (officeInfo?.code) {
			void import('@/utils/dataLayerConfig')
				.then(({ dataLayerConfig }) => {
					dataLayerConfig({
						event: 'facility_set',
						brand: config.name,
						website_selected_facility_id: officeInfo?.code,
						website_selected_facility_name: officeInfo?.name,
					});
					return true;
				})
				.catch(console.error);
		}
	}, [officeInfo?.code, config.name, officeInfo?.name]);

	return (
		<Box
			id="site-header" // allows other components find header by ID
			data-test-id="section_header"
			sx={getHeaderWrapperStyles(
				showMobileHeaderLocationBar,
				showHeaderNavMenu,
				showHeaderWithLocationNav,
				showTopAlertBannerMessages,
				showBottomAlertBannerMessages
			)}
		>
			{isLargeDevice ? (
				<HeaderDesktop
					withOfficeDetails={withOfficeDetails}
					headerData={appData.header}
					logoUrl={logoUrl || appData.logo?.url}
					navigationBar={navigationBar}
					inlineNavigation={inlineNavigation}
					topLinks={topLinks}
					logoPosition={logoPosition}
					isDarkHeader={appData.header?.darkHeader || false}
					hidePrimaryActionButton={hidePrimaryActionButton}
					hideLocation={hideLocation}
					showHeaderNavMenu={showHeaderNavMenu}
					logoHref={logoHref}
					locationNavigationLinks={locationNavigationLinks}
					phoneNumber={phoneNumber}
					withFixedHeader={withFixedHeader}
					showLanguageToggle={showLanguageToggle}
					useHeaderLocationCards={useHeaderLocationCards}
					googleMapsForLocationCards={googleMapsForLocationCards}
					topAlertBannerMessages={topAlertBanner?.alertMessagesCollection}
					bottomAlertBannerMessages={bottomAlertBanner?.alertMessagesCollection}
					showTopAlertBannerMessages={showTopAlertBannerMessages}
					showBottomAlertBannerMessages={showBottomAlertBannerMessages}
					variant={variant}
				/>
			) : (
				<HeaderMobile
					withOfficeDetails={withOfficeDetails}
					headerData={appData.header}
					logoUrl={appData.logoMobile?.url || appData.logo?.url}
					isFullScreenMenu={isFullScreenMenu}
					withMenuCTA={withMenuCTA}
					hidePrimaryActionButton={hidePrimaryActionButton}
					hideLocation={hideLocation}
					showHeaderNavMenu={showHeaderNavMenu}
					logoHref={logoHref}
					locationNavigationLinks={locationNavigationLinks}
					phoneNumber={phoneNumber}
					withFixedHeader={withFixedHeader}
					showMobileHeaderLocationBar={showMobileHeaderLocationBar}
					showLanguageToggle={showLanguageToggle}
					hideHamburgerMenuToggleButton={hideHamburgerMenuToggleButton}
					useHeaderLocationCards={useHeaderLocationCards}
					googleMapsForLocationCards={googleMapsForLocationCards}
					topAlertBannerMessages={topAlertBanner?.mobileAlertMessagesCollection}
					bottomAlertBannerMessages={bottomAlertBanner?.mobileAlertMessagesCollection}
					showTopAlertBannerMessages={showTopAlertBannerMessages}
					showBottomAlertBannerMessages={showBottomAlertBannerMessages}
					variant={variant}
				/>
			)}
		</Box>
	);
}
